import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogPosts } from "@/mocks/homeData";

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const post = blogPosts.find((p) => p.slug === slug);

  const localeMap: Record<string, string> = { en: "en-GB", fr: "fr-FR", de: "de-DE" };
  const dateLocale = localeMap[i18n.language] || "en-GB";

  if (!post) {
    return (
      <div className="min-h-screen bg-background-950 flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold text-background-50 mb-3">{t("blog.notFoundTitle")}</h1>
        <p className="text-background-50/60 mb-6">{t("blog.notFoundText")}</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          {t("blog.backToBlog")}
        </Link>
      </div>
    );
  }

  const otherPosts = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);
  const title = t(`posts.${post.key}.title`);
  const category = t(`posts.${post.key}.category`);
  const readTime = t(`posts.${post.key}.readTime`);
  const content = t(`posts.${post.key}.content`);

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />

      {/* Article Header */}
      <section className="relative pt-28 md:pt-36 pb-8 md:pb-12 px-4 md:px-6 max-w-3xl mx-auto">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-background-50/50 hover:text-primary-400 text-sm transition-colors mb-6 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          {t("blog.backToAll")}
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-medium text-primary-400 bg-primary-500/10 px-3 py-1 rounded-full whitespace-nowrap">
            {category}
          </span>
          <span className="text-xs text-background-50/40">
            {readTime}
          </span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 tracking-tight leading-tight">
          {title}
        </h1>

        <div className="flex items-center gap-3 mt-5 pb-8 border-b border-background-700/50">
          <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center">
            <i className="ri-user-line text-primary-400 text-sm"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-background-50">{post.author}</p>
            <p className="text-xs text-background-50/40">
              {new Date(post.date).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-4 md:px-6 pb-10 md:pb-14 max-w-3xl mx-auto">
        <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden">
          <img
            src={post.image}
            alt={title}
            title={title}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 md:px-6 pb-16 md:pb-20 max-w-3xl mx-auto">
        <article
          className="max-w-none
            [&_h2]:font-heading [&_h2]:text-primary-400 [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
            [&_h3]:font-heading [&_h3]:text-primary-400 [&_h3]:font-semibold [&_h3]:text-lg md:[&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-3
            [&_p]:text-background-50/75 [&_p]:leading-relaxed [&_p]:text-sm md:[&_p]:text-base [&_p]:mb-5
            [&_strong]:text-background-50 [&_strong]:font-semibold
            [&_ul]:my-5 [&_ul]:pl-6 [&_ul]:list-disc [&_ul]:space-y-2
            [&_ol]:my-5 [&_ol]:pl-6 [&_ol]:list-decimal [&_ol]:space-y-2
            [&_li]:text-background-50/75 [&_li]:text-sm md:[&_li]:text-base [&_li]:leading-relaxed [&_li]:pl-1
            [&_li]:marker:text-primary-400 [&_a]:text-primary-400 [&_a]:underline [&_a]:underline-offset-2 [&_a]:transition-colors hover:[&_a]:text-primary-300"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </section>

      {/* Share & CTA */}
      <section className="px-4 md:px-6 pb-16 md:pb-20 max-w-3xl mx-auto">
        <div className="border-t border-background-700/50 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-background-50 mb-1">{t("blog.helpfulTitle")}</p>
            <p className="text-xs text-background-50/50">{t("blog.helpfulText")}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="w-9 h-9 rounded-full bg-background-800 border border-background-700 flex items-center justify-center text-background-50/60 hover:text-primary-400 hover:border-primary-400/30 transition-colors cursor-pointer"
              aria-label={t("blog.shareX")}
            >
              <i className="ri-twitter-x-line text-sm"></i>
            </button>
            <button
              className="w-9 h-9 rounded-full bg-background-800 border border-background-700 flex items-center justify-center text-background-50/60 hover:text-primary-400 hover:border-primary-400/30 transition-colors cursor-pointer"
              aria-label={t("blog.shareLinkedIn")}
            >
              <i className="ri-linkedin-line text-sm"></i>
            </button>
            <button
              className="w-9 h-9 rounded-full bg-background-800 border border-background-700 flex items-center justify-center text-background-50/60 hover:text-primary-400 hover:border-primary-400/30 transition-colors cursor-pointer"
              aria-label={t("blog.copyLink")}
            >
              <i className="ri-link text-sm"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="px-4 md:px-6 pb-20 md:pb-28 max-w-6xl mx-auto">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-7">
          {t("blog.relatedTitle")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {otherPosts.map((related) => (
            <Link
              key={related.id}
              to={`/blog/${related.slug}`}
              className="group bg-background-900/60 rounded-2xl border border-background-700 overflow-hidden transition-all duration-300 hover:border-primary-400/30 hover:-translate-y-1 cursor-pointer"
            >
              <div className="w-full aspect-[16/10] overflow-hidden">
                <img
                  src={related.image}
                  alt={t(`posts.${related.key}.title`)}
                  title={t(`posts.${related.key}.title`)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] md:text-xs font-medium text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {t(`posts.${related.key}.category`)}
                  </span>
                  <span className="text-[10px] md:text-xs text-background-50/40">
                    {t(`posts.${related.key}.readTime`)}
                  </span>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-background-50 leading-snug line-clamp-2 group-hover:text-primary-400 transition-colors">
                  {t(`posts.${related.key}.title`)}
                </h3>
                <p className="text-xs md:text-sm text-background-50/50 mt-2 line-clamp-2 leading-relaxed">
                  {t(`posts.${related.key}.excerpt`)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}